/**
 * App-aware module configuration facade.
 * Routes load/save/UI helpers to HPRMS or HOMS by appType.
 * Keeps the two products' definitions and localStorage keys separate.
 */

import * as hprms from "./moduleConfigurationUtils";
import * as homs from "./homsModuleConfigurationUtils";

export function isHomsAppType(appType) {
  return homs.isHomsAppType(appType);
}

export function isHprmsAppType(appType) {
  return hprms.isHprmsAppType(appType);
}

/** True when this appType has a company module-config system. */
export function isModuleConfigAppType(appType) {
  return isHprmsAppType(appType) || isHomsAppType(appType);
}

function apiFor(appType) {
  return isHomsAppType(appType) ? homs : hprms;
}

export function getModuleDefinitions(appType) {
  return isHomsAppType(appType)
    ? homs.HOMS_MODULE_DEFINITIONS
    : hprms.HPRMS_MODULE_DEFINITIONS;
}

export function getModulePacks(appType) {
  return isHomsAppType(appType) ? homs.HOMS_MODULE_PACKS : hprms.HPRMS_MODULE_PACKS;
}

export function getSectionHelp(appType) {
  return isHomsAppType(appType) ? homs.HOMS_SECTION_HELP : hprms.HPRMS_SECTION_HELP;
}

export function loadAppModuleConfiguration(companyName, instanceId, appType) {
  return apiFor(appType).loadModuleConfiguration(companyName, instanceId, appType);
}

export function saveAppModuleConfiguration(companyName, instanceId, config, appType) {
  return apiFor(appType).saveModuleConfiguration(companyName, instanceId, config, appType);
}

export function normalizeAppModuleConfiguration(config, meta = {}) {
  return apiFor(meta.appType).normalizeModuleConfiguration(config, meta);
}

export function getPagesForModule(appType, moduleKey) {
  return apiFor(appType).getPagesForModule(moduleKey);
}

export function getFieldGroupsForModule(appType, moduleKey) {
  return apiFor(appType).getFieldGroupsForModule(moduleKey);
}

export function moduleHasConfigurableDetails(appType, moduleKey) {
  return apiFor(appType).moduleHasConfigurableDetails(moduleKey);
}

export function tryToggleModule(appType, modules, moduleKey, enable) {
  return apiFor(appType).tryToggleModule(modules, moduleKey, enable);
}

export function tryTogglePage(appType, pages, pageKey, enable) {
  const api = apiFor(appType);
  if (typeof api.tryTogglePage === "function") {
    return api.tryTogglePage(pages, pageKey, enable);
  }
  const next = { ...(pages || {}), [pageKey]: enable };
  return { ok: true, pages: next, message: "" };
}

export function getBlockingPageDependents(appType, pageKey, pages) {
  const api = apiFor(appType);
  if (typeof api.getBlockingPageDependents === "function") {
    return api.getBlockingPageDependents(pageKey, pages);
  }
  return [];
}

export function getTurnOffPageBlockMessage(appType, pageKey, pages) {
  const api = apiFor(appType);
  if (typeof api.getTurnOffPageBlockMessage === "function") {
    return api.getTurnOffPageBlockMessage(pageKey, pages);
  }
  return "";
}

export function applyModulePack(appType, packId, currentModules) {
  return apiFor(appType).applyModulePack(packId, currentModules);
}

export function getTurnOffBlockMessage(appType, moduleKey, modules) {
  return apiFor(appType).getTurnOffBlockMessage(moduleKey, modules);
}

export function getBlockingDependents(appType, moduleKey, modules) {
  return apiFor(appType).getBlockingDependents(moduleKey, modules);
}

export { MODULE_SETTINGS_PATH } from "./moduleConfigurationUtils";
